import type { Vec3 } from './types';

export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export const length = (a: Vec3): number => Math.hypot(a[0], a[1], a[2]);
export const distance = (a: Vec3, b: Vec3): number => length(sub(a, b));
export const normalize = (a: Vec3): Vec3 => {
  const magnitude = length(a) || 1;
  return scale(a, 1 / magnitude);
};
export const midpoint = (a: Vec3, b: Vec3): Vec3 => scale(add(a, b), 0.5);
export const centroid = (a: Vec3, b: Vec3, c: Vec3): Vec3 => scale(add(add(a, b), c), 1 / 3);
export const triangleNormal = (a: Vec3, b: Vec3, c: Vec3): Vec3 => normalize(cross(sub(b, a), sub(c, a)));
export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export function angleDegrees(a: Vec3, b: Vec3): number {
  return Math.acos(clamp(dot(normalize(a), normalize(b)), -1, 1)) * 180 / Math.PI;
}

export function pointSegmentClosest(point: Vec3, a: Vec3, b: Vec3): { point: Vec3; t: number; distance: number } {
  const edge = sub(b, a);
  const denom = dot(edge, edge);
  const t = denom > 0 ? clamp(dot(sub(point, a), edge) / denom, 0, 1) : 0;
  const closest = add(a, scale(edge, t));
  return { point: closest, t, distance: distance(point, closest) };
}

// Real-Time Collision Detection, Christer Ericson: closest point on triangle.
export function closestPointOnTriangle(p: Vec3, a: Vec3, b: Vec3, c: Vec3): Vec3 {
  const ab = sub(b, a); const ac = sub(c, a); const ap = sub(p, a);
  const d1 = dot(ab, ap); const d2 = dot(ac, ap);
  if (d1 <= 0 && d2 <= 0) return a;
  const bp = sub(p, b); const d3 = dot(ab, bp); const d4 = dot(ac, bp);
  if (d3 >= 0 && d4 <= d3) return b;
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) return add(a, scale(ab, d1 / (d1 - d3)));
  const cp = sub(p, c); const d5 = dot(ab, cp); const d6 = dot(ac, cp);
  if (d6 >= 0 && d5 <= d6) return c;
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) return add(a, scale(ac, d2 / (d2 - d6)));
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    return add(b, scale(sub(c, b), (d4 - d3) / ((d4 - d3) + (d5 - d6))));
  }
  const denom = 1 / (va + vb + vc);
  return add(a, add(scale(ab, vb * denom), scale(ac, vc * denom)));
}

export function quantizedPoint(point: Vec3, epsilon = 1e-6): string {
  return point.map((value) => Math.round(value / epsilon)).join(',');
}

export function edgeKey(a: Vec3, b: Vec3): string {
  const qa = quantizedPoint(a); const qb = quantizedPoint(b);
  return qa < qb ? `${qa}|${qb}` : `${qb}|${qa}`;
}

export function cloneVec(point: Vec3): Vec3 { return [...point] as Vec3; }
